import {
  type SelectionsActions,
  type SelectedValueCoordinate,
  type SelectionIntent,
  type SelectionMutationOptions
} from '@databases/selections-api-types';
import { type AnyAction, type Dispatch } from 'redux';

/**
 * Shape of the cellSelectionsSlice actions.
 * Matches the actions from @databases/redux/cellSelections/slice.
 */
interface CellSelectionsSliceActions {
  selectFields: (payload: {
    contentId: string;
    fieldIds: string[];
    clear: boolean;
    focusFieldId: string | undefined;
    clearSelectedValues?: boolean;
  }) => AnyAction;

  selectEntries: (payload: {
    contentId: string;
    entryIds: string[];
    clear: boolean;
    focusEntryId: string | undefined;
    clearSelectedValues?: boolean;
  }) => AnyAction;

  selectValues: (payload: {
    contentId: string;
    values: SelectedValueCoordinate[];
    clear: boolean;
    focusValue: SelectedValueCoordinate | undefined;
  }) => AnyAction;

  deselectAll: () => AnyAction;

  setSoftSelection: (payload: { value: boolean }) => AnyAction;

  setHoverDeleteButton: (payload: { value: 'fields' | 'entries' | null }) => AnyAction;

  setFirstSelectedValue: (payload: {
    value: SelectedValueCoordinate | null;
  }) => AnyAction;
}

export interface LegacyReduxAdapterOptions {
  dispatch: Dispatch;
  contentId: string;
  sliceActions: CellSelectionsSliceActions;
}

/**
 * Adapter that implements SelectionsActions using the legacy Redux cellSelections slice.
 *
 * This adapter wraps Redux dispatch calls to provide the same interface as the
 * new SelectionsApi-based adapter, enabling gradual migration without breaking changes.
 */
export class LegacyReduxAdapter implements SelectionsActions {
  #dispatch: Dispatch;
  #contentId: string;
  #actions: CellSelectionsSliceActions;

  constructor(options: LegacyReduxAdapterOptions) {
    this.#dispatch = options.dispatch;
    this.#contentId = options.contentId;
    this.#actions = options.sliceActions;
  }

  selectFields(fieldIds: string[], options: SelectionMutationOptions = {}): void {
    const { clear = true, clearSelectedValues = true } = options;

    this.#dispatch(
      this.#actions.selectFields({
        contentId: this.#contentId,
        fieldIds,
        clear,
        focusFieldId: fieldIds[fieldIds.length - 1],
        clearSelectedValues
      })
    );
  }

  selectEntries(entryIds: string[], options: SelectionMutationOptions = {}): void {
    const { clear = true, clearSelectedValues = true } = options;

    this.#dispatch(
      this.#actions.selectEntries({
        contentId: this.#contentId,
        entryIds,
        clear,
        focusEntryId: entryIds[entryIds.length - 1],
        clearSelectedValues
      })
    );
  }

  selectValues(
    values: SelectedValueCoordinate[],
    options: SelectionMutationOptions = {}
  ): void {
    const { clear = true } = options;

    this.#dispatch(
      this.#actions.selectValues({
        contentId: this.#contentId,
        values,
        clear,
        focusValue: values[values.length - 1]
      })
    );
  }

  deselectAll(): void {
    this.#dispatch(this.#actions.deselectAll());
  }

  setSelectionIntent(intent: SelectionIntent): void {
    if (intent.type === 'delete') {
      this.#dispatch(this.#actions.setSoftSelection({ value: false }));
      this.#dispatch(this.#actions.setHoverDeleteButton({ value: intent.target }));
    } else if (intent.type === 'softSelection') {
      this.#dispatch(this.#actions.setHoverDeleteButton({ value: null }));
      this.#dispatch(this.#actions.setSoftSelection({ value: true }));
    }
  }

  clearSelectionIntent(): void {
    this.#dispatch(this.#actions.setHoverDeleteButton({ value: null }));
    this.#dispatch(this.#actions.setSoftSelection({ value: false }));
  }

  setFirstSelectedValue(value: SelectedValueCoordinate | null): void {
    this.#dispatch(this.#actions.setFirstSelectedValue({ value }));
  }
}
